import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt, TrendingDown, TrendingUp, Pause, Eye, AlertCircle, ShieldAlert, Activity } from 'lucide-react';

const TYPE_ICONS = {
    invoice_gap:           Receipt,
    sales_drop:            TrendingDown,
    sales_spike:           TrendingUp,
    zero_sales_streak:     Pause,
    revenue_share_anomaly: ShieldAlert,
    volume_anomaly:        Activity,
};

const SEVERITY_ORDER = { critical: 5, high: 4, medium: 3, warning: 2, low: 1, info: 0 };

function statusOf(alert) {
    if (alert.resolved) return 'resolved';
    if (alert.acknowledged) return 'acknowledged';
    return 'active';
}

function summaryOf(alert, t) {
    if (alert.message) return alert.message;
    const data = alert.data || {};
    const tenant = alert.tenant_name || alert.tenant_id || t('tenant');
    switch (alert.alert_type) {
        case 'sales_spike':
            return `${t('Spike at {{tenant}}', { tenant })}: ${formatNum(data.value)} (z=${(data.z_score || 0).toFixed(2)})`;
        case 'sales_drop':
            return `${t('Drop at {{tenant}}', { tenant })}: ${formatNum(data.value)} (z=${(data.z_score || 0).toFixed(2)})`;
        case 'zero_sales_streak':
            return t('Zero sales streak at {{tenant}}', { tenant });
        case 'invoice_gap': {
            const dets = data.details || {};
            return `${t('Invoice gap at {{tenant}}', { tenant })}${dets.gap_size ? ` (${t('{{count}} missing', { count: dets.gap_size })})` : ''}`;
        }
        case 'revenue_share_anomaly':
            return alert.message || t('Flight-risk tier change at {{tenant}}', { tenant });
        case 'volume_anomaly':
            return alert.message || t('Volume anomaly at {{tenant}}', { tenant });
        default:
            return alert.title || alert.alert_type || t('Anomaly');
    }
}

function formatNum(n) {
    if (n == null || isNaN(n)) return '—';
    if (Math.abs(n) >= 1000) return Math.round(n).toLocaleString();
    return Number(n).toFixed(0);
}

/**
 * Paginated, sortable table of anomaly alerts. Click a row to open detail panel.
 */
export default function AnomaliesTable({ alerts, loading, total, page, pageSize, onPage, selectedId, onSelect }) {
    const { t } = useTranslation();
    const [sort, setSort] = useState({ key: 'detected_at', dir: 'desc' });

    const sorted = useMemo(() => {
        const list = Array.isArray(alerts) ? [...alerts] : [];
        list.sort((a, b) => {
            let av, bv;
            if (sort.key === 'severity') {
                av = SEVERITY_ORDER[a.severity] || 0;
                bv = SEVERITY_ORDER[b.severity] || 0;
            } else {
                av = new Date(a.detected_at || a.created_at || 0).getTime();
                bv = new Date(b.detected_at || b.created_at || 0).getTime();
            }
            return sort.dir === 'desc' ? bv - av : av - bv;
        });
        return list;
    }, [alerts, sort]);

    const toggleSort = (key) => {
        setSort(prev => prev.key === key
            ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
            : { key, dir: 'desc' });
    };

    const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 25)));

    return (
        <div className="v3-anomalies-table-wrap">
            <table className="v3-anomalies-table">
                <thead>
                    <tr>
                        <th style={{ width: 28 }} />
                        <th style={{ width: 36 }} />
                        <th>{t('Tenant')}</th>
                        <th>{t('Summary')}</th>
                        <th
                            className="sortable"
                            onClick={() => toggleSort('detected_at')}
                        >{t('Detected')} {sort.key === 'detected_at' ? (sort.dir === 'desc' ? '↓' : '↑') : ''}</th>
                        <th
                            className="sortable"
                            onClick={() => toggleSort('severity')}
                        >{t('Severity')} {sort.key === 'severity' ? (sort.dir === 'desc' ? '↓' : '↑') : ''}</th>
                        <th>{t('Status')}</th>
                        <th style={{ width: 60 }} />
                    </tr>
                </thead>
                <tbody>
                    {loading && Array.from({ length: 6 }).map((_, i) => (
                        <tr key={`sk-${i}`}>
                            <td colSpan={8} style={{ padding: 0 }}>
                                <div className="v3-anomalies-skeleton" />
                            </td>
                        </tr>
                    ))}
                    {!loading && sorted.length === 0 && (
                        <tr>
                            <td colSpan={8} className="v3-anomalies-empty">
                                {t('No anomalies match these filters.')}
                            </td>
                        </tr>
                    )}
                    {!loading && sorted.map((a) => {
                        const Icon = TYPE_ICONS[a.alert_type] || AlertCircle;
                        const status = statusOf(a);
                        const sev = a.severity || 'low';
                        const isSel = selectedId && (a.id === selectedId || a._id === selectedId);
                        const id = a.id || a._id;
                        return (
                            <tr
                                key={id}
                                className={isSel ? 'selected' : ''}
                                onClick={() => onSelect(a)}
                            >
                                <td><span className={`v3-cc-dot ${sev}`} aria-hidden="true" /></td>
                                <td><Icon size={16} strokeWidth={1.75} /></td>
                                <td>{a.tenant_name || a.tenant_id || '—'}</td>
                                <td className="summary" title={summaryOf(a, t)}>{summaryOf(a, t)}</td>
                                <td>{a.detected_at ? new Date(a.detected_at).toLocaleString() : '—'}</td>
                                <td style={{ textTransform: 'capitalize' }}>{t(sev)}</td>
                                <td>
                                    <span className={`v3-anomalies-status-badge ${status}`}>{t(status)}</span>
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        className="v3-btn v3-btn-ghost"
                                        onClick={(e) => { e.stopPropagation(); onSelect(a); }}
                                        style={{ padding: '4px 8px' }}
                                    ><Eye size={14} /></button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <div className="v3-anomalies-pager">
                <span>{total ? (total === 1
                    ? t('{{count}} alert', { count: total })
                    : t('{{count}} alerts', { count: total })) : ''}</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button onClick={() => onPage(page - 1)} disabled={page <= 1}>{t('Previous')}</button>
                    <span>{t('Page {{page}} of {{totalPages}}', { page, totalPages })}</span>
                    <button onClick={() => onPage(page + 1)} disabled={page >= totalPages}>{t('Next')}</button>
                </div>
            </div>
        </div>
    );
}
